import moment, { Moment } from 'moment';
import React, {
  createRef,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import { Calendar } from '../DateRangePicker/Calendar';
import { DateRangePickerContext, TimeDisplayMode } from '../DateRangePicker/context';
import { DateInput } from '../DateRangePicker/DateRangeInput/DateInput';
import { TimeInput } from '../DateRangePicker/DateRangeInput/TimeInput';
import { Focus, FocusPosition, MomentRanges, RangeIndex } from '../DateRangePicker/types';
import { Flex } from '../Flex';
import { Pane } from '../Pane';

export interface DatePickerProps {
  timeDisplayMode?: TimeDisplayMode;
  /**
   * The selected date.
   */
  value: Date | null;
  /**
   * Function that is called when the date changes.
   */
  onChange: (value: Date) => void;
  /**
   * The minimum selectable date. If provided, days before this date will be disabled.
   */
  minDate?: Date;
  /**
   * The maximum selectable date. If provided, days after this date will be disabled.
   */
  maxDate?: Date;
  /**
   * Optional function that controls whether a given day is disabled. Useful for
   * limiting selection to a minimum of 3 days, for example.
   */
  isDayDisabled?: (day: Date) => boolean;
  /**
   * Optional function to render an info tooltip for a given day. Useful for visualizing
   * an orgs retention period, for example.
   */
  renderInfoTooltip?: (day: Date) => ReactNode;

  /**
   * If `true`, all inputs and days are readOnly.
   *
   * Defaults to `false`.
   */
  readOnly?: boolean;

  /**
   * If `true`, dates will be in UTC instead of the user's local timezone.
   *
   * Defaults to `false`.
   */
  utc?: boolean;

  includeTimeInputs?: boolean;
}

export interface DatePickerHandle {
  focus: () => void;
}

type InputRef = {
  blur: () => void;
  focus: () => void;
};

const DatePicker = forwardRef<DatePickerHandle, DatePickerProps>(
  (
    {
      timeDisplayMode = 'auto',
      value,
      onChange,
      minDate,
      maxDate,
      isDayDisabled,
      renderInfoTooltip,
      readOnly = false,
      utc = false,
      includeTimeInputs = false,
    },
    ref,
  ) => {
    const makeMoment = utc ? moment.utc : moment;
    const inputRef = useMemo(() => createRef<InputRef>(), []);
    const timeRef = useMemo(() => createRef<InputRef>(), []);
    const momentValue = useMemo(() => (value ? makeMoment(value) : null), [makeMoment, value]);
    const onMomentChange = useCallback(
      (value: Moment) => {
        if (includeTimeInputs && momentValue) {
          value.set({
            hour: momentValue.hour(),
            minute: momentValue.minute(),
            second: momentValue.second(),
            millisecond: momentValue.get('millisecond'),
          });
        }
        onChange(value.toDate());
      },
      [includeTimeInputs, momentValue, onChange],
    );

    const [minMoment, maxMoment] = useMemo(
      () => [minDate ? makeMoment(minDate) : undefined, maxDate ? makeMoment(maxDate) : undefined],
      [makeMoment, maxDate, minDate],
    );

    const isMomentDayDisabled = useMemo(
      () => (!isDayDisabled ? undefined : (day: Moment) => isDayDisabled(day.toDate())),
      [isDayDisabled],
    );
    const INPUTS_HEIGHT = 64;

    const renderMomentInfoTooltip = useMemo(
      () => (!renderInfoTooltip ? undefined : (day: Moment) => renderInfoTooltip(day.toDate())),
      [renderInfoTooltip],
    );

    /**
     * TODO(jcarroll2007): consider adding more information into the context to simplify the prop drilling that existed before
     * adding the context.
     */
    const contextValue = useMemo(
      () => ({
        includeTimeInputs,
        timeDisplayMode: includeTimeInputs ? 'never' : timeDisplayMode,
        readOnly,
        v2: includeTimeInputs,
      }),
      [includeTimeInputs, timeDisplayMode, readOnly],
    );

    const momentRanges = useMemo(
      (): MomentRanges => [
        { start: momentValue, end: momentValue },
        { start: null, end: null },
      ],
      [momentValue],
    );
    const focus = useMemo((): Focus => [RangeIndex.Primary, FocusPosition.Start], []);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => inputRef.current?.focus(),
      }),
      [inputRef],
    );

    // autofocus input on mount
    useEffect(() => {
      inputRef.current?.focus();
    }, [inputRef]);

    return (
      <DateRangePickerContext.Provider value={contextValue}>
        <Pane
          data-component="DatePicker"
          width="min-content"
          height={1}
          testId="date-range-picker"
          {...identifyComponent('date-picker')}
        >
          <Flex column alignItems="stretch" style={{ height: '100%' }}>
            <Box height={INPUTS_HEIGHT} padding={16} display="flex" flexDirection="row" gap={16}>
              <DateInput
                ref={inputRef}
                date={momentValue}
                onChange={onMomentChange}
                onFocus={() => null}
                onFocusNext={() => {
                  inputRef.current?.blur();
                  if (includeTimeInputs) {
                    timeRef.current?.focus();
                  }
                }}
              />
              {includeTimeInputs && (
                <Box flexShrink={0}>
                  <TimeInput
                    ref={timeRef}
                    time={momentValue?.format('hh:mm A').toString() || '12:00 AM'}
                    onFocus={() => null}
                    onFocusNext={() => timeRef.current?.blur()}
                    onChange={(time: string) => {
                      const momentTime = moment(time, 'hh:mm A');
                      if (momentValue) {
                        momentValue.set({
                          hour: momentTime.get('hour'),
                          minute: momentTime.get('minute'),
                          second: momentTime.get('second'),
                          millisecond: momentTime.get('millisecond'),
                        });
                        return onMomentChange(momentValue);
                      }
                    }}
                    rangeKey={0}
                    positionKey={FocusPosition.StartTime}
                  />
                </Box>
              )}
            </Box>
            <Box height={`calc(100% - ${INPUTS_HEIGHT}px)`}>
              <Calendar
                ranges={momentRanges}
                focus={focus}
                onDayClick={onMomentChange}
                minDate={minMoment}
                maxDate={maxMoment}
                isDayDisabled={isMomentDayDisabled}
                renderInfoTooltip={renderMomentInfoTooltip}
              />
            </Box>
          </Flex>
        </Pane>
      </DateRangePickerContext.Provider>
    );
  },
);
DatePicker.displayName = 'DatePicker';

const MemoizedDatePicker = React.memo(DatePicker);

export { MemoizedDatePicker as DatePicker };
